import debounce from "lodash/debounce";
import scrollTo from "../utils/scrollTo";
import { Easings, linearScale } from "@scrolly/renderer";
import PlayablePresentation from "./PlayablePresentation";
import {
    calculateSlidePosition,
    getSlideFontSizeByScreenWidth,
    slidePositionBreakpoints,
} from "./calculateSlidePosition";

const IS_HTML_MODE = typeof document === "undefined";

/**
 * Callback invoked by the renderer when a user-observable event happens
 * (currently: `"storyNavigation"` fires when the scroll crosses a slide
 * boundary). Consumers plug in their own tracking — the renderer stays
 * analytics-agnostic.
 *
 * Payload shape per event name:
 * - `"storyNavigation"`: `{ slide: number; totalSlides: number; type: "scroll" }`
 */
export type VisualStoryEventHandler = (name: string, payload: unknown) => void;

const SCROLL_SPEED = 1400;
// we use the offset to combat the fact, that we need to scroll the "dead zone"
const SCROLL_TIME_OFFSET = 400;
// Intro-Tween: nach Cover-Start scrollt der Viewer programmatisch zu
// Slide-1.textCenter, damit der User die erste Animation einmal abspielen
// sieht und versteht: „aha, das ist scroll-getrieben". Länger als SCROLL_SPEED,
// damit Layer-Übergänge wahrnehmbar sind.
// Iteration 2026-05-28: 2200ms (gar nicht spürbar wegen iOS-Cache) → 4000ms
// (debug-bump, klar sichtbar) → 3000ms („etwas schneller wäre besser") →
// 2000ms (final, knackig).
const INTRO_DURATION = 2000;
/** Sentence-Bloom intro: auto-scroll only until this many quote words are visible. */
const SENTENCE_BLOOM_INTRO_VISIBLE_WORDS = 3;
// when a user makes a click in `FAST_CLICK` ms again, we are in fast mode
const FAST_CLICK = 800;
// when the user is `FAST_SCROLL_SLIDES_AHEAD` slides head with scrolling, we are in fast mod
const FAST_SCROLL_SLIDES_AHEAD = 1;
// after `SLOW_SCROLL_TIMEOUT`ms we show the animations again
const SLOW_SCROLL_TIMEOUT = 1200;

interface SlidePosition {
    top: number;
    animateAreaFrom: number;
    textCenter: number;
    height: number;
}

export class VisualStoryStore {
    post: PlayablePresentation | undefined = undefined;

    scrollTop = 0;
    dom: HTMLElement | undefined = undefined;
    viewerFrame: HTMLElement;
    heightFrame: HTMLElement;
    /**
     * Scroll-Source-Polarität (Plan-2 iOS-Fix 2026-06-01). DEFAULT "container"
     * = heutiges Verhalten (Renderer liest viewerFrame.scrollTop, Höhe aus
     * heightFrame). Nur der public/-Immersive-Full-Page-Reader opted auf
     * "window" (window.scrollY + window.innerHeight) — entkoppelt vom
     * dvh/URL-Bar-Hack UND vom verzögerten nested clientHeight (3ce09c7-Pfad).
     */
    scrollSource: "container" | "window" = "container";
    // Gecachte Frame-Höhe (Snapshot pro Breite, s. getFrameDimensions): gated den
    // resize-Recompute beim iOS-URL-Bar-Kollaps, damit die Slides nicht reflowen.
    private cachedFrameHeight: number | null = null;
    private cachedFrameWidth: number | null = null;
    stopUpdate = false;
    slidePositions: Array<SlidePosition> = [];
    /* The following offset was hardcoded to be a high number initially.
     * This lets the page always scroll down to a position where the credits
     * should be visible.
     */
    creditsScrollOffset = 1000000000000000;
    innerHeight = 800;
    innerWidth = 320;

    // fast scrolling/clicking attributes
    fastScroll = true;
    lastSlideIndex = -1;
    lastButtonClick: Date = new Date();
    lastSlideTimestamp: number = +new Date();
    fastClick = false;
    makeSlideSlowAgainTimeout: number | undefined;
    stopped = true;
    rafId: number | null = null;

    heightSubscribers: (() => void)[] = [];
    widthSubscribers: (() => void)[] = [];
    beforeStorySubscribers: (() => void)[] = [];
    afterStorySubscribers: (() => void)[] = [];
    cancelScroll? = () => {};
    private afterStoryLatched = false;
    private isProgrammaticScroll = false;
    debouncedUpdate: () => void = debounce(
        () => {
            const changed = this.regenerateBoundsCache();
            if (changed) {
                this.regenerateSlidePositionsCache();
                // initalize again as we might overwrite the textWidth
                setTimeout(() => {
                    this.regenerateSlidePositionsCache();
                }, 10);
            }
        },
        100,
        { trailing: true }
    );

    subscribeToHeightChanges = (callback: () => void) => {
        this.heightSubscribers.push(callback);
        return () => {
            this.heightSubscribers = this.heightSubscribers.filter((sub) => sub !== callback);
        };
    };

    subscribeToWidthChanges = (callback: () => void) => {
        this.widthSubscribers.push(callback);
        return () => {
            this.widthSubscribers = this.widthSubscribers.filter((sub) => sub !== callback);
        };
    };

    subscribeToBeforeStoryChanges = (callback: () => void) => {
        this.beforeStorySubscribers.push(callback);
        return () => {
            this.beforeStorySubscribers = this.beforeStorySubscribers.filter((sub) => sub !== callback);
        };
    };

    subscribeToAfterStoryChanges = (callback: () => void) => {
        this.afterStorySubscribers.push(callback);
        return () => {
            this.afterStorySubscribers = this.afterStorySubscribers.filter((sub) => sub !== callback);
        };
    };

    emitHeightChanges = () => {
        this.heightSubscribers.forEach((sub) => sub());
    };

    emitWidthChanges = () => {
        this.widthSubscribers.forEach((sub) => sub());
    };

    emitBeforeStoryChanges = () => {
        this.beforeStorySubscribers.forEach((sub) => sub());
    };

    emitAfterStoryChanges = () => {
        this.afterStorySubscribers.forEach((sub) => sub());
    };

    heightSnapshot = () => {
        return this.innerHeight;
    };

    widthSnapshot = () => {
        return this.innerWidth;
    };

    afterStorySnapshot = () => {
        return this.afterStory;
    };

    beforeStorySnapshot = () => {
        return this.beforeStory;
    };

    private onEvent?: VisualStoryEventHandler;

    constructor(
        viewerFrame: HTMLElement,
        heightRefFrame: HTMLElement,
        onEvent?: VisualStoryEventHandler,
        scrollSource: "container" | "window" = "container",
    ) {
        this.viewerFrame = viewerFrame;
        this.heightFrame = heightRefFrame;
        this.onEvent = onEvent;
        this.scrollSource = scrollSource;
        this.nextSlide = this.nextSlide.bind(this);
        this.previousSlide = this.previousSlide.bind(this);
    }

    /**
     * Scroll-Source-Accessoren (Plan-2). "container" = viewerFrame (heute);
     * "window" = document-scroll (entkoppelt vom dvh/URL-Bar + nested clientHeight).
     */
    getScrollTop(): number {
        return this.scrollSource === "window"
            ? window.scrollY
            : this.viewerFrame.scrollTop;
    }

    getMaxScroll(): number {
        return this.scrollSource === "window"
            ? document.documentElement.scrollHeight - window.innerHeight
            : this.viewerFrame.scrollHeight - this.viewerFrame.clientHeight;
    }

    getScrollContext(): Window | HTMLElement {
        return this.scrollSource === "window" ? window : this.viewerFrame;
    }

    setScrollTopValue(value: number): void {
        if (this.scrollSource === "window") {
            window.scrollTo(0, value);
        } else {
            this.viewerFrame.scrollTop = value;
        }
    }

    /**
     * Frame-Rect für die Slide-Top-Berechnung (initialisePositions).
     * "container": heightFrame-Rect (Slide-Tops relativ zum Container).
     * "window": documentElement-Rect (.top = -window.scrollY → Slide-Tops
     * document-absolut, konsistent mit window.scrollY).
     */
    getFrameRect(): DOMRect {
        return this.scrollSource === "window"
            ? document.documentElement.getBoundingClientRect()
            : this.heightFrame.getBoundingClientRect();
    }

    getFrameDimensions() {
        // Roh-Maße je Scroll-Source (window-Branch heute inert — container überall,
        // s. ViewerChrome Iteration 3).
        let rawHeight: number;
        let width: number;
        if (this.scrollSource === "window") {
            rawHeight = window.innerHeight;
            width = window.innerWidth;
        } else {
            const rect = this.heightFrame.getBoundingClientRect();
            rawHeight = rect.height;
            width = rect.width;
        }
        // Per-Breite-Höhe-Cache (Plan-2 iOS-Jump-Fix, Iteration 3): Der iOS-URL-Bar-
        // Kollaps ändert die Höhe OHNE Breiten-Wechsel. Ohne Cache liefert
        // regenerateBoundsCache dann changed=true → regenerateSlidePositionsCache →
        // Slides reflowen → End-Sprung. Mit Cache bleibt die Höhe pro Breite konstant
        // → changed=false → kein Recompute → kein Sprung. NUR valide (>0) Höhen
        // cachen (sonst lockt der Cache die 3ce09c7-Null ein → Video-Timer-Crash).
        // Re-Snapshot nur bei echtem Breiten-/Orientierungswechsel. Trade-off: Slides
        // für den Initial-State dimensioniert → bei collapsed leichte Lücke unten,
        // aber KEIN Sprung (der akzeptable Failure-Mode).
        if (
            rawHeight > 0 &&
            (this.cachedFrameHeight === null || width !== this.cachedFrameWidth)
        ) {
            this.cachedFrameHeight = rawHeight;
            this.cachedFrameWidth = width;
        }
        return { height: this.cachedFrameHeight ?? rawHeight, width };
    }

    /**
     * Synchronous remeasure after iOS bfcache restore / external-link back.
     * Prefer visualViewport over layout dvh when they diverge — otherwise the
     * sticky canvas is shorter than the shell and a dead strip appears at the bottom.
     */
    syncFrameDimensions(): boolean {
        this.cachedFrameHeight = null;
        this.cachedFrameWidth = null;

        let rawHeight: number;
        let width: number;
        const vv =
            typeof window !== "undefined" ? window.visualViewport : null;

        if (this.scrollSource === "window") {
            rawHeight = vv?.height ?? window.innerHeight;
            width = vv?.width ?? window.innerWidth;
        } else {
            const rect = this.heightFrame.getBoundingClientRect();
            rawHeight = rect.height;
            width = rect.width;
            if (vv?.height && vv.height > 0 && Math.abs(vv.height - rawHeight) > 4) {
                rawHeight = vv.height;
                width = vv.width || width;
            }
        }

        if (rawHeight > 0) {
            this.cachedFrameHeight = rawHeight;
            this.cachedFrameWidth = width;
        }

        const changed = this.regenerateBoundsCache();
        if (changed) {
            this.regenerateSlidePositionsCache();
        }
        return changed;
    }

    /** @deprecated Prefer syncFrameDimensions — debounced async remeasure. */
    invalidateFrameHeightCache() {
        this.syncFrameDimensions();
    }

    regenerateBoundsCache() {
        const oldHeight = this.innerHeight;
        const oldWidth = this.innerWidth;
        const { height, width } = this.getFrameDimensions();
        this.innerHeight = height;
        this.innerWidth = width;
        this.emitHeightChanges();
        this.emitWidthChanges();
        if (!IS_HTML_MODE && oldHeight && oldWidth) {
            return this.innerHeight !== oldHeight || this.innerWidth !== oldWidth;
        }
        return true;
    }

    start = (dom: TODOAny) => {
        this.dom = dom;
        this.stopUpdate = false;
        this.regenerateBoundsCache();
        const syncTimerToScroll = () => {
            if (this.stopped) {
                return;
            }

            if (this.stopUpdate || IS_HTML_MODE || !this.dom || this.isProgrammaticScroll) {
                this.rafId = requestAnimationFrame(syncTimerToScroll);
                return;
            }

            const scrollTop = this.getScrollTop();
            const diff = scrollTop - this.scrollTop;

            if (!this.post) {
                throw new Error("Called VisualStory#start before setting post.");
            }

            const { presentationRender, slidePlayer } = this.post;

            this.scrollTop += diff;

            const { index: slideIndex, position } = this.getSlidePositionAtScroll(this.scrollTop);
            const timeDiff = +new Date() - this.lastSlideTimestamp;

            if (this.lastSlideIndex !== slideIndex) {
                this.lastSlideIndex = slideIndex;
                this.lastSlideTimestamp = +new Date();
                this.onEvent?.("storyNavigation", {
                    slide: slideIndex,
                    totalSlides: presentationRender.slides.length,
                    type: "scroll",
                });
            }

            const scrollingFast = Math.abs(slidePlayer.currentSlide - slideIndex) > FAST_SCROLL_SLIDES_AHEAD;
            const scrollingSlow = timeDiff > SLOW_SCROLL_TIMEOUT;

            if (scrollingFast) {
                this.fastScroll = true;
            }

            if (scrollingSlow) {
                this.fastScroll = false;
            }

            const animationSlide = presentationRender.slides[slideIndex];
            const previousSlide =
                slideIndex > 0 ? presentationRender.slides[slideIndex - 1] : presentationRender.slides[slideIndex];
            // using textcenter works much better than position.animationArea.from
            // with this, the prev/next buttons work as expected when a slide is scrollable
            const timer = linearScale(
                [position.animateAreaFrom, position.textCenter],
                [previousSlide.from, animationSlide.from],
                { clamp: true }
            )(this.scrollTop);
            // always use scrolling
            slidePlayer.setTimer(timer);
            this.emitBeforeStoryChanges();
            this.emitAfterStoryChanges();
            this.rafId = requestAnimationFrame(syncTimerToScroll);
        };

        if (!IS_HTML_MODE) {
            window.addEventListener("resize", this.debouncedUpdate);
        }

        // This is an ugly hack which should be resolved later.
        // The problem is due to the innerHeight usage instead of 100vh we
        // make sure to initalise the positions AFTER regenerateBoundsCache
        // is called which means the component rerenders.
        // TODO Investigage whether this hack is still needed
        setTimeout(() => {
            this.regenerateSlidePositionsCache();
            setTimeout(() => {
                syncTimerToScroll();
                // registering the styles will change the positions, so we need to reapply this
                setTimeout(() => {
                    this.regenerateSlidePositionsCache();
                }, 10);
            });
        });
        this.stopped = false;
    };

    regenerateSlidePositionsCache() {
        if (!this.dom || this.isProgrammaticScroll) {
            return;
        }
        const scrollTop = this.getScrollTop(); // window.pageYOffset || document.documentElement.scrollTop;

        const articleRect = this.dom.getElementsByClassName("article-container")[0].getBoundingClientRect();
        // Container mode: (bcr.top − rectFrame.top) gives position relative to
        // the container's visible edge → add scrollTop to get scroll-space.
        // Window mode: rectFrame.top = −scrollY, so the subtraction already
        // yields the page-absolute position → adding scrollTop double-counts.
        const scrollTopForPosition = this.scrollSource === "window" ? 0 : scrollTop;
        const slides = this.dom.getElementsByClassName("slide-container");

        if (!this.post) {
            throw new Error("Called VisualStory#initialisePositions before setting post.");
        }

        const { renderer, presentationRender } = this.post;

        const slidePositions: Array<SlidePosition> = [];
        const rectFrame = this.getFrameRect();
        for (let i = 0; i < slides.length; i += 1) {
            const { safeAreaSizeXRelative, bounds } = renderer.calculateBounds();
            const presentationSlide = presentationRender.slides[i];
            const dom = slides[i];
            const animateArea = dom.querySelector(".slide-animate");
            const contentArea: HTMLDivElement = dom.querySelector(".slide-content")!;
            const boundingClientRect = dom.getBoundingClientRect();
            const rect: { top: number; height: number } = {
                top: boundingClientRect.top - rectFrame.top, // get top relative to the viewer frame (not the viewport)
                height: boundingClientRect.height,
            };
            const boundingClientRectAnimate = animateArea!.getBoundingClientRect();
            const rectAnimate: { top: number; height: number } = {
                top: boundingClientRectAnimate.top - rectFrame.top, // get top relative to the viewer frame (not the viewport)
                height: boundingClientRectAnimate.height,
            };
            const rectContent = contentArea
                ? contentArea.getBoundingClientRect()
                : {
                      height: 0,
                      width: 0,
                  };
            const { height } = rect;
            const top = rect.top + scrollTopForPosition;

            const useMobile = bounds.newWidth < slidePositionBreakpoints.ipad;

            const position = calculateSlidePosition(
                presentationSlide.options || presentationRender.presentationOptions.slideOptions,
                safeAreaSizeXRelative,
                bounds.newWidth,
                350,
                300,
                useMobile
            );
            const animateAreaFrom = rectAnimate.top + scrollTopForPosition;

            slidePositions.push({
                // when no text is there, we don't have "dead space scrolling"
                textCenter:
                    top + rectAnimate.height - (contentArea ? this.innerHeight / 2 - rectContent.height / 2 : 0),
                animateAreaFrom,
                top,
                height,
            });
            if (i === slides.length - 1) {
                const maxScroll = this.getMaxScroll();

                this.creditsScrollOffset = Math.min(top + height + this.innerHeight, maxScroll);
            }

            if (contentArea) {
                if (useMobile) {
                    contentArea.style.transform = "";
                    contentArea.style.width = "100%";
                } else {
                    const translateX =
                        position.left !== undefined
                            ? position.left + position.margin
                            : articleRect.width - position.right! - rectContent.width - position.margin;
                    contentArea.style.transform = `translateX(${translateX}px)`;
                    contentArea.style.width = `${position.width}px`;
                    contentArea.style.fontSize = getSlideFontSizeByScreenWidth(bounds.newWidth);
                }
            }
        }

        this.slidePositions = slidePositions;
    }

    getSlidePositionAtScroll(scrollTop: number): {
        index: number;
        position: SlidePosition;
    } {
        if (this.slideCount === 0) {
            return {
                index: 0,
                position: {
                    top: 0,
                    animateAreaFrom: 0,
                    textCenter: 0,
                    height: 0,
                },
            };
        }
        let slidePositionIndex = Math.max(
            0,
            this.slidePositions.findIndex(({ top, height }) => top >= scrollTop - height)
        );

        if (scrollTop > this.slidePositions.at(-1)!.top) {
            slidePositionIndex = this.slideCount - 1;
        }

        return {
            index: slidePositionIndex,
            position: this.slidePositions[slidePositionIndex],
        };
    }

    stop() {
        window.removeEventListener("resize", this.debouncedUpdate);
        if (this.cancelScroll) {
            this.cancelScroll();
        }
        this.stopUpdate = true;
        this.stopped = true;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    setPost(post: PlayablePresentation | undefined) {
        this.post = post;
    }

    handleSlideSpeed() {
        clearTimeout(this.makeSlideSlowAgainTimeout);
        const lastClick = Number(new Date()) - Number(this.lastButtonClick);
        // to consecutive click in less than 500ms
        const fast = lastClick < FAST_CLICK;
        this.fastClick = this.fastClick || fast;
        // if (this.fastClick) {
        //     this.makeSlideSlowAgainTimeout = setTimeout(() => {
        //         runInAction(() => {
        //             this.fastClick = false;
        //         });
        //     }, SLOW_CLICK_TIMEOUT);
        // }
        this.lastButtonClick = new Date();
    }

    paginateSlide(oldSlideIndex: number, newSlideIndex: number) {
        const newSlidePosition = this.slidePositions[newSlideIndex];

        if (!newSlidePosition || !this.post) {
            return;
        }

        const { slides } = this.post.presentationRender;
        const oldSlide = slides[oldSlideIndex];
        const newSlide = slides[newSlideIndex];

        const animationDuration = Math.max(Math.abs(oldSlide.from - newSlide.from) + SCROLL_TIME_OFFSET, SCROLL_SPEED);

        const newTop = newSlidePosition.textCenter;

        this.cancelScroll = scrollTo(newTop, {
            context: this.getScrollContext(),
            duration: animationDuration,
            easing: Easings.quadInOut,
        })?.cancel;
    }

    previousSlide() {
        this.handleSlideSpeed();

        if (!this.post) {
            throw new Error("Called VisualStory#previousSlide before setting post.");
        }

        const oldSlideIndex = this.post.slidePlayer.currentGoalSlide;
        const newSlideIndex = this.afterStory ? this.slideCount - 1 : oldSlideIndex - 1;
        this.paginateSlide(oldSlideIndex, newSlideIndex);
    }

    nextSlide() {
        this.handleSlideSpeed();

        if (!this.post) {
            throw new Error("Called VisualStory#nextSlide before setting post.");
        }

        const oldSlideIndex = this.post.slidePlayer.currentGoalSlide;
        const newSlideIndex = oldSlideIndex + 1;
        this.paginateSlide(oldSlideIndex, newSlideIndex);
    }

    scrollToStartSlide(opts?: { intro?: boolean }) {
        this.handleSlideSpeed();
        if (opts?.intro) {
            this.playIntroSequence();
            return;
        }
        this.goToSlide(0);
    }

    /**
     * Scroll target for the post-cover intro tween.
     * Generic scrollys: slide[1].textCenter (first content animation).
     * Sentence-Bloom: ~3 words into the slide-bloom runway, not its end.
     */
    resolveIntroScrollTarget(): number | undefined {
        // post is PlayablePresentation — slides live on presentationRender, not post.data.data.
        const slides = this.post?.presentationRender?.slides ?? [];
        const bloomIdx = slides.findIndex((s) => s?.id === 'slide-bloom');
        if (bloomIdx >= 0) {
            const pos = this.slidePositions[bloomIdx];
            if (!pos) return undefined;

            const quoteText = slides.find((s) => s?.id === 'slide-2')?.text?.text ?? '';
            const wordCount = quoteText
                .replace(/<[^>]+>/g, ' ')
                .trim()
                .split(/\s+/)
                .filter(Boolean).length;
            const totalWords = Math.max(wordCount, 1);
            // Match RendererWebGL typewriter: floor(progress * (totalWords + 0.5))
            const fraction = Math.min(
                0.2,
                (SENTENCE_BLOOM_INTRO_VISIBLE_WORDS + 0.5) / (totalWords + 0.5),
            );
            const runway = pos.textCenter - pos.animateAreaFrom;
            return pos.animateAreaFrom + runway * fraction;
        }

        return (this.slidePositions[1] ?? this.slidePositions[0])?.textCenter;
    }

    /**
     * Intro-Tween nach Cover-Start: scrollt programmatisch zu
     * Slide-1.textCenter (Fallback Slide-0 wenn Single-Slide). Dauert
     * INTRO_DURATION ms, easing quadInOut. User-Input (touchstart, wheel,
     * mousedown, keydown) cancelt den Tween sofort und detacht die
     * Listener — verhindert das „User vs. Tween"-Race, das Plan-N-Resume
     * auf der UX-Ebene gekillt hatte.
     *
     * Sentence-Bloom exception: presentation slide index 1 is `slide-bloom`
     * (the typewriter runway ending at bloomEnd). Its textCenter is the END of
     * the quote — scrolling there shows almost the full text. Bloom intro
     * stops ~3 words into the runway instead.
     *
     * Listener cleanup auch bei natürlichem Tween-Ende (scroll.promise.finally).
     */
    playIntroSequence() {
        const target = this.resolveIntroScrollTarget();
        if (target == null) return;

        const maxScroll = this.getMaxScroll();
        const safeTop = Math.min(target, maxScroll);

        const cancel = () => {
            this.cancelScroll?.();
            detach();
        };
        const detach = () => {
            this.viewerFrame.removeEventListener("wheel", cancel);
            this.viewerFrame.removeEventListener("touchstart", cancel);
            this.viewerFrame.removeEventListener("mousedown", cancel);
            window.removeEventListener("keydown", cancel);
        };

        this.viewerFrame.addEventListener("wheel", cancel, { passive: true });
        this.viewerFrame.addEventListener("touchstart", cancel, { passive: true });
        this.viewerFrame.addEventListener("mousedown", cancel);
        window.addEventListener("keydown", cancel);

        const scroll = scrollTo(safeTop, {
            context: this.getScrollContext(),
            duration: INTRO_DURATION,
            easing: Easings.quadInOut,
        });
        this.cancelScroll = scroll?.cancel;
        void scroll?.promise.finally(detach);
    }

    scrollToCredits() {
        this.handleSlideSpeed();

        const target = this.creditsScrollOffset;
        this.afterStoryLatched = false;
        this.isProgrammaticScroll = true;

        this.cancelScroll = scrollTo(target, {
            context: this.getScrollContext(),
            duration: SCROLL_SPEED,
            easing: Easings.quadOut,
        })?.cancel;

        // Safari-Fix
        setTimeout(() => {
            requestAnimationFrame(() => {
                const maxScroll = this.getMaxScroll();

                const safeTarget = Math.min(target, maxScroll);

                this.setScrollTopValue(safeTarget);
                this.scrollTop = safeTarget;
                this.isProgrammaticScroll = false;
            });
        }, SCROLL_SPEED + 50);
    }

    goToSlide(currentSlideIndex: TODOAny) {
        this.handleSlideSpeed();
        const slide = this.slidePositions[currentSlideIndex];
        if (!slide) return;

        const maxScroll = this.getMaxScroll();
        const safeTop = Math.min(slide.textCenter, maxScroll);

        this.cancelScroll = scrollTo(safeTop, {
            context: this.getScrollContext(),
            duration: SCROLL_SPEED,
            easing: Easings.quadOut,
        })?.cancel;
    }

    get slideCount() {
        return this.slidePositions.length;
    }

    get beforeStory() {
        if (this.slideCount === 0) {
            return true;
        }
        return this.scrollTop < this.innerHeight;
    }

    get afterStory() {
        if (this.afterStoryLatched) {
            return true;
        }

        if (this.slideCount === 0) {
            return false;
        }

        const lastSlide = this.slidePositions.at(-1)!;
        const isAfter = lastSlide.top + lastSlide.height <= this.scrollTop;

        if (isAfter) {
            this.afterStoryLatched = true;
        }

        return isAfter;
    }
}

export default VisualStoryStore;
